import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { DsButton } from '@frontend/ui/button';
import {
    CommonMessages,
    CookieName,
    CookieService,
    DeviceFingerPrint,
    DeviceFingerprintService,
    DynamicHtmlDirective,
    LoginRedirectInfo,
    LoginResponseHandlerService,
    LoginStoreService,
    MessageLifetime,
    MessageQueueService,
    MessageScope,
    NavigationService,
    ToastrQueueService,
    ToastrSchedule,
    ToastrType,
    TrackingDirective,
    UserEvent,
    UserLoggingInEvent,
    UserLoginEvent,
    UserService,
} from '@frontend/vanilla/core';
import { LhHeaderBarComponent } from '@frontend/vanilla/features/header-bar';
import { MessagePanelComponent } from '@frontend/vanilla/features/message-panel';
import { ReCaptchaComponent, ReCaptchaValueAccessorDirective } from '@frontend/vanilla/features/recaptcha';
import { FormFieldComponent, ValidationHelperService, Validators } from '@frontend/vanilla/shared/forms';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { LoginConfig } from '@frontend/vanilla/shared/login';
import { Subject, merge } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LoginContentService } from './login-content.service';
import { LoginResourceService } from './login-resource.service';
import { LoginTrackingService } from './login-tracking.service';

@Component({
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ReCaptchaComponent,
        LhHeaderBarComponent,
        MessagePanelComponent,
        ReCaptchaValueAccessorDirective,
        DynamicHtmlDirective,
        TrackingDirective,
        FormFieldComponent,
        IconCustomComponent,
        DsButton,
    ],
    selector: 'lh-login-oauth',
    templateUrl: 'login-oauth.html',
})
export class LoginOAuthComponent implements OnInit, OnDestroy {
    formGroup: UntypedFormGroup;
    showPassword = false;

    private deviceFingerPrint: DeviceFingerPrint;
    private readonly unsubscribe = new Subject<void>();

    private readonly cookieService = inject(CookieService);
    private readonly data = inject(MAT_DIALOG_DATA);
    private readonly deviceFingerprintService = inject(DeviceFingerprintService);
    private readonly dialogRef = inject(MatDialogRef<LoginOAuthComponent>);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly loginResource = inject(LoginResourceService);
    private readonly loginResponseHandlerService = inject(LoginResponseHandlerService);
    private readonly loginStore = inject(LoginStoreService);
    private readonly messageQueue = inject(MessageQueueService);
    private readonly toastrQueueService = inject(ToastrQueueService);
    private readonly trackingService = inject(LoginTrackingService);
    private readonly validationHelper = inject(ValidationHelperService);
    readonly commonMessages = inject(CommonMessages);
    readonly loginConfig = inject(LoginConfig);
    readonly loginContentService = inject(LoginContentService);
    readonly navigation = inject(NavigationService);
    readonly user = inject(UserService);

    get disabled(): boolean {
        return this.formGroup.disabled;
    }

    get reCaptchaControl(): UntypedFormControl | null {
        return this.formGroup ? (this.formGroup.get('captcharesponse') as UntypedFormControl) : null;
    }

    ngOnInit() {
        const isPostLogin = this.cookieService.get(CookieName.LoginHint);
        this.trackingService.trackLoginWithProvider(this.data.oAuthProvider, {
            actionEvent: 'Displayed',
            positionEvent: 'link/login screen',
            eventDetails: `User is detected as existing and is asked to link the account with ${this.data.oAuthProvider} login`,
        });
        this.trackingService.trackLoginWithProvider(this.data.oAuthProvider, {
            actionEvent: 'Message Displayed',
            positionEvent: isPostLogin ? 'Post Login' : 'link/login screen',
            eventDetails: this.loginContentService.content.messages?.OAuthIdInfoText || '',
        });
        this.formGroup = this.formBuilder.group({
            username: this.formBuilder.control(this.loginStore.LastVisitor || '', this.validationHelper.createValidators('usernameLogin')),
            password: this.formBuilder.control('', [Validators.required]),
            captcharesponse: this.formBuilder.control(''),
        });

        this.deviceFingerPrint = this.deviceFingerprintService.get();
        this.messageQueue.clear({ clearPersistent: true, scope: MessageScope.Login });
        this.messageQueue.addInfo(this.loginContentService.content.messages?.OAuthIdInfoText || '', MessageLifetime.Single, MessageScope.Login);

        merge(this.navigation.locationChange, this.user.events.pipe(filter((e: UserEvent) => e instanceof UserLoginEvent)))
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => this.closeDialog());
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    close() {
        this.trackingService.trackLoginWithProvider(this.data.oAuthProvider, {
            actionEvent: 'Close (x) - Clicked',
            positionEvent: 'link/login screen',
            eventDetails: 'User is detected as existing clicks on Close(x) on Link/Login Screen',
        });
        this.closeDialog();
        this.navigation.goToLastKnownProduct();
    }

    togglePassword() {
        this.showPassword = !this.showPassword;
    }

    login() {
        if (!this.formGroup) {
            return;
        }

        this.trackingService.trackLoginWithProvider(this.data.oAuthProvider, {
            actionEvent: 'Link  Accounts and Login CTA - Clicked',
            positionEvent: 'link/login screen',
            eventDetails: 'User is detected as existing clicks on Link accounts and Login CTA on Link/Login Screen',
        });

        this.formGroup.disable();
        this.user.triggerEvent(new UserLoggingInEvent());
        const model = this.formGroup.value;
        Object.assign(model, { fingerprint: this.deviceFingerPrint }, this.data);

        this.loginResource.login(model, { showSpinner: false, messageQueueScope: MessageScope.Login }).subscribe({
            next: (data) => {
                this.loginResponseHandlerService.handle(data).then((redirectInfo: LoginRedirectInfo) => {
                    this.toastrQueueService.add(ToastrType.LinkAccount, { schedule: ToastrSchedule.AfterNextNavigation });
                    this.dialogRef.close();
                    redirectInfo.goTo();
                });
            },
            error: (rejected) => {
                this.trackingService.trackLoginWithProvider(this.data.oAuthProvider, {
                    actionEvent: 'Error Message Displayed',
                    positionEvent: 'link/login screen',
                    eventDetails: rejected?.posApiErrorMessage || '',
                });
                this.formGroup.get('password')?.setValue('');
                this.formGroup.get('password')?.markAsTouched();
                this.formGroup.enable();
            },
        });
    }

    private closeDialog() {
        this.dialogRef.close();
        this.messageQueue.clear({ clearPersistent: true, scope: MessageScope.Login });
    }
}
